import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';

/**
 * Indicative local pricing — BUILD-PLAN §56.
 *
 * Shows a customer roughly what a price means in the money they think in,
 * while every figure remains priced and charged in the base currency.
 *
 * The distinction is load-bearing, not cosmetic. The card is debited in the
 * base currency at the bank's own rate on the day, so presenting a converted
 * figure as "the amount you will pay" is a misquote, and a chargeback. Every
 * conversion is therefore rendered as an approximation beside the real price,
 * never instead of it (§86).
 */

export interface ExchangeRates {
  base: string;
  rates: Record<string, number>;
  indicative: boolean;
  settlementCurrency: string;
}

export const currencyKeys = {
  rates: ['currency', 'rates'] as const,
};

/**
 * ISO 4217 minor-unit exponent — mirrors ExchangeRateService::exponentFor.
 *
 * Only the exceptions are listed; two decimals is the rule. Getting this
 * wrong is a factor-of-100 pricing error, not a rounding one: JPY has no
 * minor unit, so treating ¥8995 as two decimals shows ¥89.95.
 */
const EXPONENTS: Record<string, number> = {
  JPY: 0, KRW: 0, ISK: 0, CLP: 0, VND: 0, XOF: 0, XAF: 0, UGX: 0, RWF: 0, PYG: 0,
  KWD: 3, BHD: 3, OMR: 3, JOD: 3, TND: 3, IQD: 3, LYD: 3,
};

export function exponentFor(currency: string): number {
  return EXPONENTS[currency.toUpperCase()] ?? 2;
}

export function useExchangeRates() {
  return useQuery({
    queryKey: currencyKeys.rates,
    queryFn: () => api.get<ExchangeRates>('/currency/rates'),
    // Rates are cached for twelve hours server-side; asking more often than
    // once an hour per session buys nothing.
    staleTime: 60 * 60 * 1000,
    // A missing conversion is a small loss of comfort. Retrying hard for one
    // is not worth the requests.
    retry: false,
  });
}

export interface LocalPrice {
  /** Formatted in the destination's currency, e.g. "≈ $121.60". */
  formatted: string;
  currency: string;
}

/**
 * Convert base minor units into a formatted local price.
 *
 * Returns null — meaning "show nothing" — when the currency matches the base,
 * when no rate is available, or when the feed was unreachable. Showing a
 * stale or invented number would be worse than showing none.
 */
export function useLocalPrice(
  baseMinor: number | null | undefined,
  currency: string | null | undefined,
  locale = 'en-GB',
): LocalPrice | null {
  const { data } = useExchangeRates();

  if (!data || baseMinor === null || baseMinor === undefined || !currency) return null;

  const target = currency.toUpperCase();

  // Nothing to say when the customer is already looking at their own money.
  if (target === data.base.toUpperCase()) return null;

  const rate = data.rates[target];

  if (!rate || rate <= 0) return null;

  const baseScale = 10 ** exponentFor(data.base);
  const amount = (baseMinor / baseScale) * rate;

  try {
    return {
      currency: target,
      formatted: new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: target,
        maximumFractionDigits: exponentFor(target),
      }).format(amount),
    };
  } catch {
    // An unknown ISO code would throw; better silent than broken.
    return null;
  }
}
